import { useEffect } from 'preact/hooks';
import { Button } from '@components/buttons';
import { IcoExclamation } from '@components/icons';
import { useCurrentUser } from 'client/lib/auth';
import { StripeCheckoutForm } from 'client/courses/components/checkout/stripe-checkout-form';
import { rpx } from 'client/lib/rpx-client';
import type { PaneProps } from '../../types';
import { WizardPane } from './wizard-pane';
import { CouponRow, PriceRow } from 'server/types';
import { StatusPill } from './status-pill';
import { CheckoutForm, PriceSummary } from '@course/components/checkout';
import * as pmtmath from 'shared/payments/math';

function PricingConfigurationError() {
  return (
    <WizardPane contentClassName="justify-center items-center gap-6">
      <div class="flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
        <IcoExclamation class="w-8 h-8 text-orange-600" />
      </div>

      <header class="text-center max-w-md">
        <h2 class="text-2xl font-bold text-gray-900 mb-3">Pricing Configuration Needed</h2>
        <p class="text-gray-600 leading-relaxed">
          The calendar service pricing hasn't been configured yet. Please contact support to set up
          your calendar pricing options.
        </p>
      </header>

      <nav class="flex flex-col gap-3 w-full max-w-sm">
        <Button
          href="mailto:support@mirasee.com?subject=Calendar Pricing Setup'"
          class="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 px-6 rounded-xl font-semibold"
        >
          Contact Support
        </Button>
        <Button
          href="/"
          class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-xl font-semibold"
        >
          Return to Homepage
        </Button>
      </nav>
    </WizardPane>
  );
}

function PaymentForm({
  price,
  coupon,
  onNext,
}: {
  price: PriceRow;
  coupon?: CouponRow;
  onNext: () => void;
}) {
  const isFree = pmtmath.isFree({ price, coupon });
  const actionText = "Start My Founder's Plan →";
  const handlePurchase = async (options: {
    priceId?: string;
    couponId?: string;
    stripePaymentMethod?: string;
  }) => {
    if (!options.priceId) {
      throw new Error('Payment information incomplete');
    }

    try {
      const result = await rpx.calendarCheckout.purchase({
        priceId: options.priceId,
        couponId: options.couponId,
        stripePaymentMethod: options.stripePaymentMethod,
      });

      return result;
    } catch (error: any) {
      // Handle case where user already has an active subscription
      if (error.data?.code === 'ALREADY_ACTIVE') {
        // User already has subscription, skip to next step
        onNext();
        return undefined; // Return undefined to indicate no further action needed
      }
      throw error;
    }
  };

  return (
    <WizardPane>
      <header class="text-center mb-8">
        <StatusPill text="Payment details" theme="purple" className="mb-4" />
        <h2 class="text-2xl font-bold text-gray-900 mb-3">Complete Your Purchase</h2>
        <p class="text-gray-600">
          Activate your Founder's plan and unlock your AI-powered coaching platform
        </p>
      </header>

      {/* Price Summary */}
      <section class="max-w-md mx-auto w-full mb-8">
        <article class="bg-gray-50 border rounded-xl p-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">CoachMira</h3>

          <ul class="text-xs text-gray-500 flex flex-col gap-1 list-disc list-inside mb-4">
            <li>CoachMira Founder Plan</li>
            <li>AI-Powered Page Builder</li>
            <li>Smart Booking & Calendar Sync</li>
            <li>Custom Domain or Subdomain</li>
            <li>All Future Platform Updates</li>
          </ul>

          <footer class="pt-3 border-t border-gray-200">
            <PriceSummary price={price} coupon={coupon} />
          </footer>
        </article>
      </section>

      <section class="max-w-md mx-auto w-full flex flex-col">
        {isFree && (
          <CheckoutForm
            onSubmit={async () => {
              await handlePurchase({
                couponId: coupon?.id,
                priceId: price.id,
              });
              return onNext();
            }}
            actionText={actionText}
          ></CheckoutForm>
        )}
        {!isFree && (
          <StripeCheckoutForm
            collectAddress={false}
            price={price}
            coupon={coupon}
            actionText={actionText}
            beginPurchase={handlePurchase}
            onPurchaseComplete={async () => onNext()}
          />
        )}
      </section>

      {!isFree && <p class="text-xs text-gray-500 text-center mt-4">You can cancel anytime</p>}
    </WizardPane>
  );
}

export function PaymentStep({ onNext, price, coupon }: PaneProps) {
  const currentUser = useCurrentUser();

  // Check if user already has an active subscription
  useEffect(() => {
    if (currentUser?.calendarTier === 'core') {
      // User already has an active subscription, skip to next step
      onNext();
    }
  }, [currentUser, onNext]);

  if (!price) {
    return <PricingConfigurationError />;
  }

  return <PaymentForm price={price} coupon={coupon} onNext={onNext} />;
}
